import { useCallback, useEffect, useRef, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { Layout } from '../components/Layout'
import { AirlinesView, type Board } from '../components/admin-airlines/AirlinesView'
import {
  type RawState,
  type Refusal,
  fetchBoard,
  fetchManualChecks,
  fetchRawAnswer,
  fetchRawList,
} from '../lib/admin-airlines'
import { useAuth } from '../lib/auth'

/** /admin/airlines: the owner's monitor of the air lines, open to a superadmin's family login only. */
export function AdminAirlinesPage() {
  const { session, loading } = useAuth()
  const [board, setBoard] = useState<Board | null>(null)
  const [refusal, setRefusal] = useState<Refusal | null>(null)
  const [all, setAll] = useState(false)
  const [open, setOpen] = useState<string | null>(null)
  const [raw, setRaw] = useState<RawState>({ status: 'idle' })
  const [shown, setShown] = useState<{ path: string; text: string } | null>(null)
  // The line open now and the answer last asked for: a late reply for anything else is dropped.
  const openNow = useRef<string | null>(null)
  const askedRaw = useRef<string | null>(null)
  const userId = session?.user.id ?? null

  useEffect(() => {
    if (loading || !userId) return
    let active = true
    ;(async () => {
      const [b, c] = await Promise.all([fetchBoard(), fetchManualChecks()])
      if (!active) return
      if (!b.ok) return setRefusal(b.refusal)
      if (!c.ok) return setRefusal(c.refusal)
      setBoard({
        generatedAt: b.data.generated_at,
        airlines: b.data.airlines,
        checks: c.data,
        accuracyRun: b.data.accuracy_run ?? null,
      })
    })()
    return () => {
      active = false
    }
  }, [loading, userId])

  const toggle = useCallback(async (prefix: string) => {
    setShown(null)
    askedRaw.current = null
    if (openNow.current === prefix) {
      openNow.current = null
      return setOpen(null)
    }
    openNow.current = prefix
    setOpen(prefix)
    setRaw({ status: 'loading' })
    const list = await fetchRawList(prefix)
    if (openNow.current !== prefix) return
    setRaw(
      list.ok
        ? { status: 'ready', ...list.data }
        : { status: 'failed', message: list.refusal.message },
    )
  }, [])

  const openRaw = useCallback(async (path: string) => {
    askedRaw.current = path
    const answer = await fetchRawAnswer(path)
    if (askedRaw.current !== path) return
    setShown({
      path,
      text: answer.ok ? JSON.stringify(answer.data, null, 2) : answer.refusal.message,
    })
  }, [])

  return (
    <Layout>
      <Helmet>
        <title>Airline monitor · Admin</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <AirlinesView
        loading={loading}
        signedIn={Boolean(session)}
        refusal={refusal}
        board={board}
        all={all}
        setAll={setAll}
        open={open}
        raw={raw}
        shown={shown}
        onToggle={toggle}
        onOpenRaw={openRaw}
      />
    </Layout>
  )
}
